import type { MapPing } from "~/shared/components/tracking-map/tracking-map.tsx";
import { isStopActivityStatus } from "~/shared/utils/tracking-status.ts";

export type TrackingUpdateLike = {
	latitude: number | null;
	longitude: number | null;
	status: string;
	location: string;
	sortDate: string;
};

export type TrackingStopLike = {
	sortOrder: number | null;
	stopType: string;
	location: string;
	latitude: number | null;
	longitude: number | null;
};

function buildTrackingPings(updates: readonly TrackingUpdateLike[]): MapPing[] {
	const sortedUpdates = [...updates].sort(
		(a, b) => Date.parse(b.sortDate || "") - Date.parse(a.sortDate || ""),
	);

	return sortedUpdates
		.filter(
			(u): u is typeof u & { latitude: number; longitude: number } =>
				u.latitude != null && u.longitude != null,
		)
		.filter((u) => !isStopActivityStatus(u.status))
		.map((u, i) => ({
			id: `tracking-${String(i)}`,
			latitude: u.latitude,
			longitude: u.longitude,
			type: "tracking" as const,
			label: `${u.location} — ${u.status}`,
			timestamp: u.sortDate,
		}));
}

function buildStopPings(stops: readonly TrackingStopLike[]): MapPing[] {
	const sorted = [...stops]
		.filter(
			(s): s is typeof s & { latitude: number; longitude: number } =>
				s.latitude != null && s.longitude != null,
		)
		.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

	let pickupCount = 0;
	let deliveryCount = 0;

	return sorted.map((s) => {
		const isPickup = s.stopType.toLowerCase() === "pickup";
		const prefix = isPickup ? "P" : "D";
		const index = isPickup ? ++pickupCount : ++deliveryCount;
		const markerLabel = `${prefix}${String(index)}`;

		return {
			id: `stop-${String(s.sortOrder)}`,
			latitude: s.latitude,
			longitude: s.longitude,
			type: isPickup ? ("pickup" as const) : ("delivery" as const),
			markerLabel,
			label: `${markerLabel} — ${s.location}`,
		};
	});
}

export function buildMapPings(
	updates: readonly TrackingUpdateLike[],
	stops: readonly TrackingStopLike[],
): MapPing[] {
	return [...buildStopPings(stops), ...buildTrackingPings(updates)];
}
